import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faPen } from '@fortawesome/free-solid-svg-icons';

/** Big date-card pair used in the Dates tab. */
export const DateRangeRow = ({ fromLabel, toLabel, from, to, days }) => (
  <div className="pds-dates__timeline">
    <div className="pds-dates__block">
      <span className="pds-dates__block-label">{fromLabel}</span>
      <div className="pds-dates__block-card">
        <span className="pds-dates__block-day">{from?.day ?? '—'}</span>
        <span className="pds-dates__block-month">
          {from ? `${from.month} ${from.year}` : ''}
        </span>
        <span className="pds-dates__block-weekday">{from?.weekday ?? ''}</span>
      </div>
    </div>
    <div className="pds-dates__connector" aria-hidden="true">
      <div className="pds-dates__connector-line" />
      {days != null && (
        <div className="pds-dates__connector-badge">
          {days}{days === 1 ? ' day' : 'd'}
        </div>
      )}
      <div className="pds-dates__connector-line" />
    </div>
    <div className="pds-dates__block">
      <span className="pds-dates__block-label">{toLabel}</span>
      <div className="pds-dates__block-card">
        <span className="pds-dates__block-day">{to?.day ?? '—'}</span>
        <span className="pds-dates__block-month">
          {to ? `${to.month} ${to.year}` : ''}
        </span>
        <span className="pds-dates__block-weekday">{to?.weekday ?? ''}</span>
      </div>
    </div>
  </div>
);

/**
 * Summary → Dates “Mission” block.
 * Admin gets a pencil. Values come only from the mission start and end fields.
 * onSave resolves true, or a string error message.
 */
const MissionDatesSection = ({
  editable = false,
  hasDates = false,
  fromDisplay = null,
  toDisplay = null,
  days = null,
  startKey = '',
  endKey = '',
  onSave,
}) => {
  const [editing, setEditing] = useState(false);
  const [draftStart, setDraftStart] = useState('');
  const [draftEnd, setDraftEnd] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const openEdit = () => {
    setDraftStart(startKey || '');
    setDraftEnd(endKey || '');
    setError('');
    setEditing(true);
  };

  const closeEdit = () => {
    if (saving) return;
    setEditing(false);
    setError('');
  };

  const runSave = async (payload) => {
    if (!onSave || saving) return;
    setSaving(true);
    setError('');
    try {
      const result = await onSave(payload);
      if (result === true) {
        setEditing(false);
        return;
      }
      setError(typeof result === 'string' && result ? result : 'Failed to save mission dates.');
    } catch (err) {
      setError(err?.message || 'Failed to save mission dates.');
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = () => {
    if (!draftStart || !draftEnd) {
      setError('Start and end are both required.');
      return;
    }
    if (draftEnd < draftStart) {
      setError('End must be on or after start.');
      return;
    }
    runSave({ mission_start_date: draftStart, mission_end_date: draftEnd });
  };

  const clearOverride = () => {
    runSave({ mission_start_date: null, mission_end_date: null });
  };

  return (
    <div className="pds-dates__section">
      <div className="pds-dates__section-title">
        <FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" />
        Mission
        <span className="pds-dates__rule" aria-hidden="true" />
        {editable && !editing ? (
          <button
            type="button"
            className="pds-dates__edit"
            onClick={openEdit}
            aria-label="Edit mission dates"
          >
            <FontAwesomeIcon icon={faPen} />
          </button>
        ) : null}
      </div>

      {editing ? (
        <div className="pds-dates__editor">
          <label className="pds-dates__editor-field">
            <span>Start</span>
            <input
              type="date"
              value={draftStart}
              onChange={(e) => setDraftStart(e.target.value)}
              disabled={saving}
            />
          </label>
          <label className="pds-dates__editor-field">
            <span>End</span>
            <input
              type="date"
              value={draftEnd}
              onChange={(e) => setDraftEnd(e.target.value)}
              disabled={saving}
            />
          </label>
          {error ? (
            <p className="pds-dates__editor-error" role="alert">{error}</p>
          ) : null}
          <div className="pds-dates__editor-actions">
            {(startKey || endKey) ? (
              <button
                type="button"
                className="pds-dates__editor-reset"
                onClick={clearOverride}
                disabled={saving}
              >
                Clear
              </button>
            ) : null}
            <button
              type="button"
              className="pds-dates__editor-cancel"
              onClick={closeEdit}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="pds-dates__editor-save"
              onClick={saveDraft}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : hasDates ? (
        <DateRangeRow
          fromLabel="Start"
          toLabel="End"
          from={fromDisplay}
          to={toDisplay}
          days={days}
        />
      ) : (
        <p className="pds-dates__none">No mission dates yet.</p>
      )}
    </div>
  );
};

export default MissionDatesSection;
